import {
    findNodeByPath,
    GITHUB_API_BASE_URL,
    GITHUB_HOSTNAME,
    GITHUB_RAW_BASE_URL,
    GITHUB_URL_PREFIX
} from '@lved/shared-core';
import type { FSNode } from '@lved/shared-core';
import type { GitHubFetchOptions } from '../types/githubFetchOptions.js';
import type { GitHubTreeItem, GitHubTreeResponse } from './githubTypes.js';

export type ParsedRepo = {
    origin?: string;
    owner?: string;
    repo?: string;
    path?: string;
    branch?: string;
    repoParam?: string;
};

export class GitHubService {
    private static readonly GITHUB_RAW_BASE: string = GITHUB_RAW_BASE_URL;
    private static readonly GITHUB_API_BASE: string = GITHUB_API_BASE_URL;

    static getRawRepoParam(url?: string): string | null {
        const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
        return url || urlParams.get('repo');
    }

    static removeRepoParamQueryAndHash(repoParam: string): string {
        return repoParam.split(/[?#]/)[0];
    }

    /**
     * Parse for the repository information from a URL or search params
     * Expected formats:
     * - ?repo=owner/repository[/path/to/directory]
     * - ?repo=owner/repo/tree/branch/path (GitHub tree URL format)
     */

    static parseRepoFromUrl(url?: string): ParsedRepo | null {
        const parsedRepo: ParsedRepo | null = GitHubService.parseCleanRepoFromUrl(url);
        if (!parsedRepo) return null;

        return {
            origin: GITHUB_HOSTNAME,
            ...parsedRepo
        };
    }

    /**
     * Parse GitHub repository URL from search params
     * Expected formats:
     * - ?repo=owner/repository[/path/to/directory]
     * - ?repo=owner/repo/tree/branch/path (GitHub tree URL format)
     */
    static parseCleanRepoFromUrl(url?: string): ParsedRepo | null {
        const rawRepoParam: string | null = GitHubService.getRawRepoParam(url);
        if (!rawRepoParam) return null;

        // Strip the leading GitHub URL, if present
        const repoParam: string = GitHubService.removeRepoParamQueryAndHash(
            rawRepoParam.replace(GITHUB_URL_PREFIX, '')
        );

        const parts: string[] = repoParam.split('/');
        if (parts.length < 2) {
            console.error(
                'Invalid repo parameter format. Expected: owner/repository[/path] or owner/repo/tree/branch/path'
            );
            return { repoParam };
        }

        // Check if this is a GitHub tree URL format: owner/repo/tree/branch/path
        if (parts.length >= 4 && parts[2] === 'tree') {
            const owner: string = parts[0];
            const repo: string = parts[1];
            const branch: string = parts[3];
            const path: string | undefined = parts.length > 4 ? parts.slice(4).join('/') : undefined;

            return { owner, repo, path, branch, repoParam };
        }

        // Standard format: owner/repo[/path]
        const owner: string = parts[0];
        const repo: string = parts[1];
        const path: string | undefined = parts.length > 2 ? parts.slice(2).join('/') : undefined;

        return { owner, repo, path, repoParam };
    }

    static getRepoParamWithUi(parsed: ParsedRepo | null, branchFallback?: string): string | null {
        if (!parsed?.owner || !parsed.repo || !parsed.repoParam) return null;

        const repoParam: string = parsed.repoParam.replace(GITHUB_URL_PREFIX, '');
        const parts: string[] = repoParam.split('/');
        let branch: string | undefined = parsed.branch ?? branchFallback;
        let path: string = parsed.path ?? '';

        if (parts.length >= 4 && parts[2] === 'tree') {
            branch = parts[3];
            path = parts.length > 4 ? parts.slice(4).join('/') : '';
        }

        if (path.endsWith('/ui') || path === 'ui') return repoParam;

        const newPath: string = path ? `${path.replace(/\/$/, '')}/ui` : 'ui';

        if (branch) return `${parsed.owner}/${parsed.repo}/tree/${branch}/${newPath}`;
        return `${parsed.owner}/${parsed.repo}/${newPath}`;
    }

    /**
     * Check if the repository is public by fetching its metadata from GitHub API
     */
    static async isPublicRepository(owner: string, repo: string): Promise<boolean> {
        try {
            const response: Response = await fetch(`${GitHubService.GITHUB_API_BASE}/repos/${owner}/${repo}`);

            if (!response.ok) {
                // GitHub intentionally returns 404 for private repositories when this anonymous check cannot see them.
                if (response.status === 404) return false;
                if (response.status === 403) {
                    throw new Error('GitHub API rate limit exceeded. Please try again later or sign in with GitHub.');
                }
                throw new Error(`Failed to check repository visibility: ${response.status} ${response.statusText}`);
            }

            return (await response.json())?.private !== true;
        } catch (error: unknown) {
            console.error(`Error checking repository visibility: ${String(error)}`);
            if (error instanceof Error && error.message.includes('GitHub API rate limit exceeded')) throw error;
            return false;
        }
    }

    /**
     * Check if the branch exists in the repository
     */
    static async branchExists(owner: string, repo: string, branch: string): Promise<boolean> {
        try {
            const response: Response = await fetch(
                `${GitHubService.GITHUB_API_BASE}/repos/${owner}/${repo}/branches/${branch}`
            );
            return response.ok;
        } catch (error: unknown) {
            console.error(`Error checking branch '${branch}': ${String(error)}`);
            return false;
        }
    }

    /**
     * Detect the default branch of a repository
     */
    static async detectDefaultBranch(owner: string, repo: string): Promise<string> {
        try {
            const response: Response = await fetch(`${GitHubService.GITHUB_API_BASE}/repos/${owner}/${repo}`);

            if (!response.ok) {
                throw new Error(`Failed to detect default branch: ${response.status} ${response.statusText}`);
            }

            return (await response.json())?.default_branch || 'main';
        } catch (error: unknown) {
            console.error(`Error detecting default branch: ${String(error)}`);
            return 'main';
        }
    }

    /**
     * Get the commit SHA for a specific branch
     */
    static async getBranchSha(owner: string, repo: string, branch: string = 'main'): Promise<string> {
        try {
            const response: Response = await fetch(
                `${GitHubService.GITHUB_API_BASE}/repos/${owner}/${repo}/branches/${branch}`
            );

            if (!response.ok) throw new Error(`Failed to get branch SHA: ${response.status} ${response.statusText}`);

            const data: { commit?: { sha?: string } } = await response.json();
            if (!data?.commit?.sha) throw new Error('Branch response missing commit SHA');
            return data.commit.sha;
        } catch (error: unknown) {
            console.error(`Error getting branch SHA: ${String(error)}`);
            throw error;
        }
    }

    /**
     * Fetch repository contents using GitHub's Tree API (most efficient method)
     * This method gets the entire repository structure in one API call
     *
     * Benefits of using the Tree API:
     * - Single API call instead of multiple recursive calls
     * - No 404 errors for subdirectories
     * - Better performance for large repositories
     * - Automatic fallback to contents API if Tree API is not available
     */
    static async fetchRepositoryContents(
        owner: string,
        repo: string,
        path: string = '',
        branch: string = 'main'
    ): Promise<FSNode[]> {
        try {
            // Always use the Tree API for efficiency, then filter for specific paths
            const allNodes: FSNode[] = await GitHubService.fetchRepositoryTree(owner, repo, branch);

            // If no specific path is requested, return all root-level items
            if (!path) return allNodes;

            // Find the specific path in the tree
            const targetNode: FSNode | null = findNodeByPath(allNodes, path);
            if (!targetNode) {
                console.warn(`Path '${path}' not found in repository tree`);
                return [];
            }

            // Return the children of the target path, or the node itself if it is a file
            return targetNode.type === 'directory' && targetNode.children ? targetNode.children : [targetNode];
        } catch (error: unknown) {
            console.error(`Error fetching repository contents: ${String(error)}`);
            throw error;
        }
    }

    /**
     * Fetch entire repository tree using GitHub's Tree API
     * This is the most efficient method as it gets all files in one API call
     */
    private static async fetchRepositoryTree(owner: string, repo: string, branch: string = 'main'): Promise<FSNode[]> {
        try {
            // Get the commit SHA for the branch
            const commitSha: string = await GitHubService.getBranchSha(owner, repo, branch);

            // Get the tree SHA from the commit
            const commitResponse: Response = await fetch(
                `${GitHubService.GITHUB_API_BASE}/repos/${owner}/${repo}/git/commits/${commitSha}`
            );

            if (!commitResponse.ok) {
                throw new Error(`Failed to get commit: ${commitResponse.status} ${commitResponse.statusText}`);
            }

            const commitData: { tree: { sha: string } } = await commitResponse.json();
            const treeSha: string = commitData?.tree?.sha;
            if (!treeSha) throw new Error('Commit response missing tree SHA');

            // Get the complete tree recursively
            const treeUrl: string = `${GitHubService.GITHUB_API_BASE}/repos/${owner}/${repo}/git/trees/${treeSha}?recursive=1`;
            console.debug(`Fetching repository tree from: ${treeUrl}`);

            const treeResponse: Response = await fetch(treeUrl);

            if (!treeResponse.ok) {
                if (treeResponse.status === 403) {
                    throw new Error(
                        'GitHub API rate limit exceeded. Please try again later or use a different repository.'
                    );
                }
                if (treeResponse.status === 404) {
                    throw new Error('Tree API not available');
                }
                throw new Error(`GitHub API error: ${treeResponse.status} ${treeResponse.statusText}`);
            }

            const treeData: GitHubTreeResponse = await treeResponse.json();

            // Check if the tree was truncated (too many files)
            if (treeData?.truncated) console.warn('Repository tree was truncated, some files may be missing');

            // Convert tree data to FSNode format
            return GitHubService.convertTreeToFSNodes(treeData.tree);
        } catch (error: unknown) {
            console.error(`Error fetching repository tree: ${String(error)}`);
            throw error;
        }
    }

    /**
     * Convert GitHub tree data to FSNode format
     */
    private static convertTreeToFSNodes(treeItems: GitHubTreeItem[]): FSNode[] {
        const fsNodes: FSNode[] = [];
        const pathMap = new Map<string, FSNode>();

        // Sort items by path to ensure directories come before their contents
        const sortedItems: GitHubTreeItem[] = treeItems.sort((a, b) => a.path.localeCompare(b.path));

        for (const item of sortedItems) {
            const pathParts: string[] = item.path.split('/');
            const name: string = pathParts[pathParts.length - 1];

            const fsNode: FSNode = {
                name,
                path: item.path,
                type: item.type === 'tree' ? 'directory' : 'file',
                size: item.size,
                children: item.type === 'tree' ? [] : undefined
            };

            // Store the node in the path map
            pathMap.set(item.path, fsNode);

            if (pathParts.length === 1) {
                // If this is a root-level item, add it to the result
                fsNodes.push(fsNode);
            } else {
                const parentPath: string = pathParts.slice(0, -1).join('/');
                const parentNode: FSNode | undefined = pathMap.get(parentPath);
                // Add to parent directory
                if (parentNode?.children) parentNode.children.push(fsNode);
            }
        }

        return fsNodes;
    }

    /**
     * Fetch file content from GitHub using raw URLs (no rate limits)
     */
    static async fetchFileContent(
        owner: string,
        repo: string,
        path: string,
        branch: string = 'main',
        options?: GitHubFetchOptions
    ): Promise<string> {
        try {
            const rawUrl: string = `${GitHubService.GITHUB_RAW_BASE}/${owner}/${repo}/${branch}/${path}`;
            console.debug('[GitHubService] Fetching file content', {
                owner,
                repo,
                branch,
                path,
                rawUrl
            });

            const response: Response = await fetch(rawUrl);

            if (!response.ok) {
                throw new Error(`Failed to fetch file content: ${response.status} ${response.statusText}`);
            }

            return await response.text();
        } catch (error: unknown) {
            const logMethod: typeof console.warn | typeof console.error = options?.suppressExpectedErrorLog
                ? console.warn
                : console.error;
            logMethod('[GitHubService] Error fetching file content', {
                owner,
                repo,
                branch,
                path,
                error: String(error),
                suppressed: Boolean(options?.suppressExpectedErrorLog)
            });
            throw error;
        }
    }

    /**
     * Fetch binary file content from GitHub using raw URLs (no rate limits)
     */
    static async fetchBinaryFileContent(
        owner: string,
        repo: string,
        path: string,
        branch: string = 'main',
        options?: GitHubFetchOptions
    ): Promise<Uint8Array> {
        try {
            const rawUrl: string = `${GitHubService.GITHUB_RAW_BASE}/${owner}/${repo}/${branch}/${path}`;
            console.debug('[GitHubService] Fetching binary file content', {
                owner,
                repo,
                branch,
                path,
                rawUrl
            });

            const response: Response = await fetch(rawUrl);

            if (!response.ok) {
                throw new Error(`Failed to fetch binary file content: ${response.status} ${response.statusText}`);
            }

            const arrayBuffer: ArrayBuffer = await response.arrayBuffer();
            return new Uint8Array(arrayBuffer);
        } catch (error: unknown) {
            const logMethod: typeof console.warn | typeof console.error = options?.suppressExpectedErrorLog
                ? console.warn
                : console.error;
            logMethod('[GitHubService] Error fetching binary file content', {
                owner,
                repo,
                branch,
                path,
                error: String(error),
                suppressed: Boolean(options?.suppressExpectedErrorLog)
            });
            throw error;
        }
    }

    /**
     * Convert GitHub repository structure to the expected manifest format
     */
    static async createManifestFromRepo(
        owner: string,
        repo: string,
        basePath: string = '',
        branch?: string
    ): Promise<{ path: string; children: FSNode[] }> {
        // Detect branch if not provided
        const detectedBranch: string = branch || (await GitHubService.detectDefaultBranch(owner, repo));
        const children: FSNode[] = await GitHubService.fetchRepositoryContents(owner, repo, basePath, detectedBranch);

        return {
            path: basePath,
            children
        };
    }

    /**
     * Check if the current URL has a repo parameter
     */
    static hasRepoParameter(): boolean {
        const repoInfo: ParsedRepo | null = GitHubService.parseCleanRepoFromUrl();
        return Boolean(repoInfo && repoInfo.owner !== undefined && repoInfo.repo !== undefined);
    }
}
